let counter = 0;

function checkPermission(permission, usersPermissions) {
    return usersPermissions.some(userPermission => {

        if (userPermission.endsWith('.*')) {
            return (permission.startsWith(userPermission.slice(0, -1)))
        }
        return (permission === userPermission);
    });

}

export default {
    namespaced: true,

    state: {
        loggedIn: false,
        user: null,
        token: null,
        permissions: null,
        market: null
    },

    getters: {
        user(state) {
            return state.user;
        },

        loggedIn(state) {
            return state.loggedIn;
        },

        token(state) {
            return state.token;
        },

        getAccessToken(state) {
            if (state.token != null) {
                return state.token.access_token;
            }
            return '';
        },

        getUserName(state) {
            if (state.user && state.user.hasOwnProperty('name')) {
                return state.user.name;
            }

            return '';
        },

        getUserEmail(state) {
            if (state.user && state.user.hasOwnProperty('email')) {
                return state.user.email;
            }

            return '';
        },

        getUserId(state) {
            if (state.user && state.user.hasOwnProperty('id')) {
                return state.user.id;
            }

            return '';
        },

        getPermissions(state) {
            return state.permissions;
        },

        getMarket(state) {
            return state.market;
        },

        userHasOnePermission: (state) => (permissionsRequired) => {
            if (permissionsRequired === undefined) {
                return false;
            }

            return permissionsRequired.some(permission => {
                return checkPermission(permission, state.permissions);
            });
        },

        userHasAllPermissions: (state) => (permissionsRequired) => {
            if (permissionsRequired === undefined) {
                return false;
            }

            return permissionsRequired.every(permission => {
                return checkPermission(permission, state.permissions);
            });
        }
    },

    mutations: {
        setIsLoggedIn(state) {
            state.loggedIn = true;
        },

        setIsLoggedOut(state) {
            state.loggedOut = false;
        },

        setToken(state, token) {
            token.expires_at = token.expires_in*1000 + Date.now();
            state.token = token;
        },

        setUser(state, user) {
            state.user = user.attributes;
            state.user.id = user.id;
        },

        reset(state) {
            state.loggedIn = false;
            state.token = null;
            state.user = null;
            state.market = null;
        },

        setMarket(state, market) {
            state.market = market || null;
        },

        setPermissions(state, permissions) {
            state.permissions = [];
            permissions.forEach((permission) => {
                if (!state.permissions.includes(permission)) {
                    state.permissions.push(permission);
                }
            });
        }
    },

    actions: {
        async authorize({ commit }, { responseType, clientId, redirectUri = null, scope = null, state = null }) {
            let data = {
                response_type: responseType,
                client_id: clientId,
                redirect_uri: redirectUri,
                scope: scope,
                state: state
            };

            if (redirectUri) {
                data.redirect_uri = redirectUri;
            }
            if (scope) {
                data.scope = scope;
            }
            if (state) {
                data.state = state;
            }

            return await this.axios.get('/oauth/authorize', {
                params: data
            });
        },

        async approveAuthorize({ commit }, { clientId, state = null }) {
            let data = {
                client_id: clientId,
                state: state
            };

            if (state) {
                data.state = state;
            }

            return await this.axios.post('/oauth/authorize', data);
        },

        async login({ commit }, { username, password, googleCode = null, shownBarcode = false }) {
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);

            if (googleCode) {
                formData.append('googleCode', googleCode);
            }
            if (shownBarcode) {
                formData.append('shownBarcode', shownBarcode);
            }

            return await this.axios.post('/oauth/token', formData).catch(function (error) {
                if (error.response && error.response.status != 400) {
                    return Promise.reject(error);
                }
                return error;
            });
        },

        async refresh({ commit, getters, dispatch }) {
            const formData = new FormData();
            formData.append('refresh_token', getters['token'].refresh_token);
            let returnResponse = await this.axios.post('/oauth/token/refresh', formData)
                .catch(function (error) {
                if (error.response && error.response.status != 401) {
                    return Promise.reject(error);
                }
                return error;
            });

            if (returnResponse.status == 200) {
                commit('setToken', returnResponse.data);
                commit('setIsLoggedIn');
                return true;
            }

            await dispatch('logout');
        },

        async setUser({ commit, dispatch }) {
            let response = await this.axios.get('/api/users/current', {
                params: {
                    fieldset: 'currentuser'
                }
            });

            if (response.status == 200) {
                commit('setUser', response.data.data);
                commit('setPermissions', response.data.data.attributes.permissions);
                // Every later request is gated on this header, and this endpoint is the
                // only ungated place it can come from.
                commit('setMarket', response.data.data.attributes.market_code);
                await dispatch('setLegacySession')
                return true;
            }

            await dispatch('logout');
        },

        async setLegacySession({dispatch}) {
            return await this.axios.post('/admin/session').catch(async function (error) {
                if (error.response && error.response.status != 403) {
                    return Promise.reject(error);
                }

                await dispatch('logout');
            });

            if (response.status == 200) {
                return true;
            }

            await dispatch('logout');
        },

        async tokenIsExpired({state}) {

            if (state.token === null) {
                return true;
            }

            return Promise.resolve(!(
                state.token.constructor === Object &&
                state.token.hasOwnProperty('expires_at') &&
                state.token.expires_at > Date.now()));
        },

        async logout() {
            await this.axios.get('/api/logout');
            await this.axios.delete('/admin/session');
            localStorage.clear();
            location.reload();
        },

        async getTokenFromSession({ commit, dispatch }) {
            try {
                let response = await this.axios.get('/auth/token');
                if (response.status === 200 && response.data.access_token) {
                    commit('setToken', {
                        access_token: response.data.access_token,
                        refresh_token: response.data.refresh_token,
                        expires_in: response.data.expires_in
                    });
                    commit('setIsLoggedIn');
                    await dispatch('setUser');
                    return true;
                }
            } catch (error) {
                console.error('Failed to get token from session', error);
            }

            await dispatch('logout');
        }
    }
}
